import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Stack } from "@mui/material";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { BalanceInformationSection } from "./balance-information-section";
import { GeneralInformationSection } from "./general-information-section";
import {
  DEFAULT_VALUES,
  VALIDATION_SCHEMA,
} from "./outlet-chart-of-account-form-constants";

export const OutletChartOfAccountForm = ({
  outletId,
  defaultValues,
  onSubmit,
  renderActionButton,
}) => {
  const methods = useForm({
    defaultValues: DEFAULT_VALUES,
    resolver: yupResolver(VALIDATION_SCHEMA),
  });

  const {
    handleSubmit,
    reset,
    watch,
    getValues,
    formState: { isSubmitting },
  } = methods;

  const hasParent = watch("hasParent");
  const selectedType = watch("type");

  const excludeSubtreeId = getValues("id");

  // Check if we're in edit mode (has an ID)
  const isEditMode = Boolean(excludeSubtreeId);

  useEffect(() => {
    if (defaultValues) {
      reset({ ...DEFAULT_VALUES, ...defaultValues });
    }
  }, [defaultValues, reset]);

  const onFormSubmit = async (formData) => {
    const payload = {
      code: formData.code,
      name: formData.name,
      type: formData.type,
      isActive: formData.isActive,
    };

    // Only add parentId if hasParent is checked and parent exists
    if (formData.hasParent && formData.parentChartOfAccount?.id) {
      payload.parentId = formData.parentChartOfAccount.id;
    }

    // Only add initial balance fields if balance is set
    if (formData.initialBalance != null && formData.initialBalance !== "") {
      payload.initialBalance = formData.initialBalance;
      payload.initialBalanceEffectiveDate = dayjs(
        formData.initialBalanceEffectiveDate
      ).format("YYYY-MM-DD");
    }

    await onSubmit(payload);
  };

  return (
    <FormProvider {...methods}>
      <Stack
        component="form"
        noValidate
        onSubmit={handleSubmit(onFormSubmit)}
        spacing={3}
      >
        <GeneralInformationSection
          outletId={outletId}
          hasParent={hasParent}
          selectedType={selectedType}
          excludeSubtreeId={excludeSubtreeId}
          isEditMode={isEditMode}
        />

        <BalanceInformationSection />

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end"
          }}>
          {renderActionButton({ isSubmitting })}
        </Box>
      </Stack>
    </FormProvider>
  );
};

OutletChartOfAccountForm.propTypes = {
  outletId: PropTypes.string.isRequired,
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  renderActionButton: PropTypes.func.isRequired,
};
